import { useEffect, useState } from "react";
import { useApiService } from "../shared/apiservice";
import { Supplier } from "../shared/types";

function parseSupplier(d: Record<string, unknown>): Supplier {
    return {
        id: Number(d.id),
        name: String(d.name ?? ""),
        code: String(d.code ?? ""),
    };
}

/**
 * Loads suppliers on demand from /suppliers using ?q= for search.
 * Call setSearchTerm / open the dropdown to trigger a fetch — nothing loads on mount.
 */
export function useSuppliers() {
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTermInternal] = useState<string | null>(null);
    const [enabled, setEnabled] = useState(false);
    const apiService = useApiService();

    function setSearchTerm(term: string) {
        setEnabled(true);
        setSearchTermInternal(term);
    }

    function ensureLoaded() {
        setEnabled(true);
        setSearchTermInternal((current) => (current === null ? "" : current));
    }

    useEffect(() => {
        if (!enabled || searchTerm === null) {
            return;
        }

        const query = searchTerm;
        let cancelled = false;

        async function fetchSuppliers() {
            setLoading(true);
            setErrors([]);

            try {
                const params = new URLSearchParams({
                    sort: "name",
                    order: "asc",
                    per_page: "50",
                });
                if (query.trim()) {
                    params.set("q", query.trim());
                }
                const response = await apiService.get(`/suppliers?${params.toString()}`);
                if (!response.ok) {
                    if (!cancelled) {
                        setErrors(["Loading suppliers failed. Please try again."]);
                    }
                    return;
                }
                const json = await response.json();
                const raw = Array.isArray(json) ? json : (json.data ?? []);
                const parsed: Supplier[] = raw.map((item: Record<string, unknown>) =>
                    parseSupplier(item)
                );
                parsed.sort((a, b) =>
                    a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
                );
                if (!cancelled) {
                    setSuppliers(parsed);
                }
            } catch (error) {
                console.error(error);
                if (!cancelled) {
                    setErrors(["Loading suppliers failed. Please try again."]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        fetchSuppliers();

        return () => {
            cancelled = true;
        };
    }, [enabled, searchTerm]);

    return {
        suppliers,
        errors,
        loading,
        searchTerm: searchTerm ?? "",
        setSearchTerm,
        ensureLoaded,
    };
}
